import { FEATURES } from 'constants/tracking';
import {
  isDashboardMode,
  isSourceQsrHardwareFlow,
} from 'helpers/search_parameter_helpers';

/**
 * Ensures the Sq.Tracking module from public-web-styles is initialized.
 *
 * @return {Promise} Promise that resolves once Sq.Tracking has initialized.
 */
const initTracking = () => {
  const waitFor = (resolve) => {
    if (Sq.Tracking && Sq.Tracking.config) {
      resolve();
    } else {
      setTimeout(() => waitFor(resolve), 100);
    }
  };

  return new Promise(waitFor);
};

const getFeature = () => {
  if (isDashboardMode() && isSourceQsrHardwareFlow()) {
    return FEATURES.SHOP_HARDWARE_FLOW;
  }

  return FEATURES.SHOP;
};

// Older tracking using EventStream 2 (normally used with trackCdp)
const trackEvent = ({ name, value, params }) => {
  initTracking().then(() => {
    Sq.Tracking.trackEvent(name, value, params);
  });
};

// Latest for tracking events with (Customer Data Platform)
const trackCdp = ({ name, params }) => {
  initTracking().then(() => {
    Sq.Tracking.cdp.track(name, params);
  });
};

/**
 * Report event to CDP using the DS logging framework.
 * @param {string} eventName Name of the event your are logging.
 * @param {Object} eventObject Metadata that details this event.
 * @param {string} eventObject.featureId
 * @param {string} eventObject.componentName General name of component this event is logged from.
 * @param {string} eventObject.featureName Human-readable name of a feature.
 * @param {string} eventObject.eventDescription Human readable description of what user interaction has triggered the event.
 * @param {string} eventObject.actionItem Text that outlines a naming convention for actions, using a "verb_object" format with lowercase and underscores.
 * @param {string} eventObject.subActionItem Additional description for an action.
 * @param {Object} eventObject.additionalParameters A customizable object for storing extra event details, like date ranges or title translations.
 */
const trackUsingLoggingFramework = (
  eventName,
  {
    featureId,
    featureName,
    componentName = '',
    eventDescription = '',
    actionItem,
    subActionItem,
    additionalParameters = {},
  },
) => {
  // We shouldn't log if we do not have an event name
  if (!eventName) return;

  initTracking().then(() => {
    const feature = getFeature();

    // TODO: For now we only want to log these new events for the Hardware QSR flow.
    // We will work as a team to refine these more for full Shop logging.
    if (feature.NAME === FEATURES.SHOP.NAME) return;

    Sq.Tracking.cdp.track(eventName, {
      feature_id: featureId || feature.ID || '',
      feature_name: featureName || feature.NAME || '',
      component_name: componentName,
      event_description: eventDescription,
      action_item: actionItem,
      sub_action_item: subActionItem,
      additional_parameters: additionalParameters,
    });
  });
};

/**
 * Report View event to CDP using the DS logging framework.
 * @param {Object} viewObject Metadata that details this event.
 * @param {string} eventObject.featureId
 * @param {string} viewObject.featureName Human-readable name of a feature
 * @param {string} viewObject.componentName General name of component this event is logged from.
 * @param {string} viewObject.eventDescription Human readable description of what user interaction has triggered the event.
 * @param {string} viewObject.actionItem Text that outlines a naming convention for actions, using a "verb_object" format with lowercase and underscores.
 * @param {string} viewObject.subActionItem Additional description for an action.
 * @param {Object} viewObject.additionalParameters A customizable object for storing extra event details, like date ranges or title translations.
 */
const trackViewFeature = ({
  featureId,
  featureName,
  componentName,
  eventDescription,
  actionItem,
  subActionItem,
  additionalParameters,
}) => {
  trackUsingLoggingFramework('View Feature', {
    featureId,
    featureName,
    componentName,
    eventDescription: eventDescription || `View ${featureName}`,
    actionItem,
    subActionItem,
    additionalParameters,
  });
};

/**
 * Report View event to CDP using the DS logging framework.
 * @param {Object} clickObject Metadata that details this event.
 * @param {string} eventObject.featureId
 * @param {string} clickObject.featureName Human-readable name of a feature
 * @param {string} clickObject.componentName General name of component this event is logged from.
 * @param {string} clickObject.eventDescription Human readable description of what user interaction has triggered the event.
 * @param {string} clickObject.actionItem Text that outlines a naming convention for actions, using a "verb_object" format with lowercase and underscores.
 * @param {string} clickObject.subActionItem Additional description for an action.
 * @param {Object} clickObject.additionalParameters A customizable object for storing extra event details, like date ranges or title translations.
 */
const trackClickFeature = ({
  featureId,
  featureName,
  componentName,
  eventDescription,
  actionItem,
  subActionItem,
  additionalParameters,
}) => {
  // We shouldn't log if we do not have an action item
  if (!actionItem) return;

  trackUsingLoggingFramework('Click Feature', {
    featureId,
    featureName,
    componentName,
    eventDescription: eventDescription || `Click ${featureName}`,
    actionItem,
    subActionItem,
    additionalParameters,
  });
};

const trackCheckoutClick = (eventDesc, orderData, additionalDetails = {}) => {
  initTracking().then(() => {
    Sq.Tracking.trackPageClick({
      action: 'checkout-click',
      description: eventDesc,
      detail: Object.assign({}, orderData, additionalDetails),
    });
  });
};

const trackGA = (command, ...args) => {
  initTracking().then(() => {
    if (
      !Sq.Tracking.config.google_universal || // GA is not enabled by the config
      (Sq.OneTrust && !Sq.OneTrust.analyticsTrackingEnabled) // Tracking is explicitly disabled by OneTrust
    ) {
      return;
    }

    // Get the tracker name from Sq.Tracking
    const trackerName = Sq.Tracking.trackerName(
      Sq.Tracking.config.google_universal,
    );

    if (window.ga) {
      ga(`${trackerName}.${command}`, ...args);
    } else {
      // If GA is enabled by the config but the global ga function is
      // undefined, this means the event queue has not been flushed. In this
      // case, we should push the tracking call to the eventQueue.
      Sq.Tracking.eventQueue.enqueue(
        () => window.ga && ga(`${trackerName}.${command}`, ...args),
      );
    }
  });
};

export {
  initTracking,
  trackEvent,
  trackCdp,
  trackGA,
  trackCheckoutClick,
  trackViewFeature,
  trackClickFeature,
};
